import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import {
  startOfMonth,
  getDay,
  getDaysInMonth,
  addMonths,
  subMonths,
  format,
  isSameDay,
  isBefore,
  isAfter,
  startOfDay,
} from "date-fns";
import { useTranslation } from "@/packages/utils";
import { useDatePickerContext } from "./DatePickerContext";
import type { DatePickerCalendarProps } from "./types";

type ViewMode = "days" | "months" | "years";

const WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

function getYearRangeStart(year: number): number {
  return year - (year % 12);
}

export function DatePickerCalendar({ className }: DatePickerCalendarProps) {
  const { t } = useTranslation(["common"]);
  const { pendingDate, setPendingDate, minDate, maxDate } =
    useDatePickerContext();

  const weekdayLabels = (t("common:datePicker.weekdays", {
    returnObjects: true,
  }) ?? WEEKDAY_KEYS) as string[];
  const monthLabels = (t("common:datePicker.months", { returnObjects: true }) ??
    []) as string[];
  const monthFullLabels = (t("common:datePicker.monthsFull", {
    returnObjects: true,
  }) ?? []) as string[];

  const [viewMonth, setViewMonth] = useState(() =>
    startOfMonth(pendingDate ?? new Date()),
  );
  const [viewMode, setViewMode] = useState<ViewMode>("days");
  const [direction, setDirection] = useState<"next" | "prev">("next");

  // Sync viewMonth when pendingDate changes to a different month (e.g. preset selection)
  // biome-ignore lint/correctness/useExhaustiveDependencies: viewMonth intentionally omitted to avoid infinite loop (effect sets viewMonth)
  useEffect(() => {
    if (pendingDate) {
      const pendingMonth = startOfMonth(pendingDate);
      if (pendingMonth.getTime() !== startOfMonth(viewMonth).getTime()) {
        setViewMonth(pendingMonth);
      }
    }
  }, [pendingDate]);

  const today = startOfDay(new Date());
  const monthStart = startOfMonth(viewMonth);
  const startWeekday = getDay(monthStart);
  const daysInMonth = getDaysInMonth(viewMonth);

  const yearRangeStart = getYearRangeStart(viewMonth.getFullYear());

  // --- Navigation handlers ---
  const handlePrev = () => {
    setDirection("prev");
    if (viewMode === "days") {
      setViewMonth((m) => subMonths(m, 1));
    } else if (viewMode === "months") {
      setViewMonth((m) => new Date(m.getFullYear() - 1, m.getMonth(), 1));
    } else {
      setViewMonth((m) => new Date(m.getFullYear() - 12, m.getMonth(), 1));
    }
  };

  const handleNext = () => {
    setDirection("next");
    if (viewMode === "days") {
      setViewMonth((m) => addMonths(m, 1));
    } else if (viewMode === "months") {
      setViewMonth((m) => new Date(m.getFullYear() + 1, m.getMonth(), 1));
    } else {
      setViewMonth((m) => new Date(m.getFullYear() + 12, m.getMonth(), 1));
    }
  };

  const handleHeaderClick = () => {
    if (viewMode === "days") setViewMode("months");
    else if (viewMode === "months") setViewMode("years");
  };

  const handleMonthSelect = (month: number) => {
    setViewMonth(new Date(viewMonth.getFullYear(), month, 1));
    setViewMode("days");
  };

  const handleYearSelect = (year: number) => {
    setViewMonth(new Date(year, viewMonth.getMonth(), 1));
    setViewMode("months");
  };

  // --- Disabled checks ---
  const isDateDisabled = (date: Date) => {
    if (minDate && isBefore(date, startOfDay(minDate))) return true;
    if (maxDate && isAfter(date, startOfDay(maxDate))) return true;
    return false;
  };

  const isMonthDisabled = (month: number) => {
    const year = viewMonth.getFullYear();
    const monthEnd = new Date(year, month + 1, 0); // last day of month
    const monthFirst = new Date(year, month, 1);
    if (minDate && isBefore(monthEnd, startOfDay(minDate))) return true;
    if (maxDate && isAfter(monthFirst, startOfDay(maxDate))) return true;
    return false;
  };

  const isYearDisabled = (year: number) => {
    const yearEnd = new Date(year, 11, 31);
    const yearFirst = new Date(year, 0, 1);
    if (minDate && isBefore(yearEnd, startOfDay(minDate))) return true;
    if (maxDate && isAfter(yearFirst, startOfDay(maxDate))) return true;
    return false;
  };

  // --- Header text ---
  const headerText =
    viewMode === "days"
      ? `${monthFullLabels[viewMonth.getMonth()] ?? ""} ${viewMonth.getFullYear()}`
      : viewMode === "months"
        ? String(viewMonth.getFullYear())
        : `${yearRangeStart} – ${yearRangeStart + 11}`;

  // --- Day cells ---
  const dayCells: (Date | null)[] = [];
  for (let i = 0; i < startWeekday; i++) {
    dayCells.push(null);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    dayCells.push(new Date(viewMonth.getFullYear(), viewMonth.getMonth(), day));
  }

  const slideOffset = direction === "next" ? 30 : -30;

  const cellBase =
    "flex items-center justify-center rounded-md transition-colors cursor-pointer";
  const cellDisabled = "text-muted opacity-40 cursor-not-allowed";
  const cellSelected = "bg-accent text-white";
  const cellHover = "hover:bg-card-hover text-secondary hover:text-primary";
  const cellToday = "ring-1 ring-accent/50";

  return (
    <div
      className={`p-3 ${className ?? ""}`}
      style={{ width: 260, minHeight: 260 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={handlePrev}
          className="p-1 rounded-md text-secondary hover:text-primary hover:bg-card-hover transition-colors"
        >
          <ChevronLeft size={16} />
        </button>
        <button
          type="button"
          onClick={handleHeaderClick}
          className="text-sm font-medium text-primary hover:text-accent transition-colors px-2 py-0.5 rounded-md hover:bg-card-hover"
        >
          {headerText}
        </button>
        <button
          type="button"
          onClick={handleNext}
          className="p-1 rounded-md text-secondary hover:text-primary hover:bg-card-hover transition-colors"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <AnimatePresence mode="wait" initial={false}>
        {/* === DAYS VIEW === */}
        {viewMode === "days" && (
          <motion.div
            key={`days-${format(viewMonth, "yyyy-MM")}`}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15 }}
          >
            <div className="grid grid-cols-7 mb-1">
              {WEEKDAY_KEYS.map((key, idx) => (
                <div
                  key={key}
                  className="text-center text-[11px] font-medium text-muted py-1"
                >
                  {weekdayLabels[idx]}
                </div>
              ))}
            </div>

            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={format(viewMonth, "yyyy-MM")}
                className="grid grid-cols-7"
                initial={{ x: slideOffset, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -slideOffset, opacity: 0 }}
                transition={{
                  duration: 0.2,
                  ease: [0.4, 0, 0.2, 1],
                }}
              >
                {dayCells.map((date, cellIndex) => {
                  if (!date) {
                    return (
                      <div key={`empty-col-${cellIndex % 7}`} className="h-8" />
                    );
                  }

                  const isToday = isSameDay(date, today);
                  const isPending = pendingDate
                    ? isSameDay(date, pendingDate)
                    : false;
                  const disabled = isDateDisabled(date);

                  return (
                    <button
                      key={date.getTime()}
                      type="button"
                      disabled={disabled}
                      onClick={() => setPendingDate(date)}
                      className={`h-8 w-8 mx-auto text-xs ${cellBase}
                            ${disabled ? cellDisabled : ""}
                            ${isPending ? cellSelected : ""}
                            ${!isPending && !disabled ? cellHover : ""}
                            ${isToday && !isPending ? cellToday : ""}
                          `}
                    >
                      {date.getDate()}
                    </button>
                  );
                })}
              </motion.div>
            </AnimatePresence>
          </motion.div>
        )}

        {/* === MONTHS VIEW === */}
        {viewMode === "months" && (
          <motion.div
            key={`months-${viewMonth.getFullYear()}`}
            className="grid grid-cols-3 gap-1"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15 }}
          >
            {monthLabels.map((label, idx) => {
              const isCurrentMonth =
                viewMonth.getMonth() === idx &&
                pendingDate?.getFullYear() === viewMonth.getFullYear() &&
                pendingDate?.getMonth() === idx;
              const isTodayMonth =
                today.getMonth() === idx &&
                today.getFullYear() === viewMonth.getFullYear();
              const disabled = isMonthDisabled(idx);

              return (
                <button
                  key={`month-${String(idx)}`}
                  type="button"
                  disabled={disabled}
                  onClick={() => handleMonthSelect(idx)}
                  className={`h-10 text-sm rounded-md ${cellBase}
                        ${disabled ? cellDisabled : ""}
                        ${isCurrentMonth ? cellSelected : ""}
                        ${!isCurrentMonth && !disabled ? cellHover : ""}
                        ${isTodayMonth && !isCurrentMonth ? cellToday : ""}
                      `}
                >
                  {label}
                </button>
              );
            })}
          </motion.div>
        )}

        {/* === YEARS VIEW === */}
        {viewMode === "years" && (
          <motion.div
            key={`years-${yearRangeStart}`}
            className="grid grid-cols-3 gap-1"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15 }}
          >
            {Array.from({ length: 12 }, (_, i) => {
              const year = yearRangeStart + i;
              const isSelectedYear = pendingDate?.getFullYear() === year;
              const isTodayYear = today.getFullYear() === year;
              const disabled = isYearDisabled(year);

              return (
                <button
                  key={year}
                  type="button"
                  disabled={disabled}
                  onClick={() => handleYearSelect(year)}
                  className={`h-10 text-sm rounded-md ${cellBase}
                        ${disabled ? cellDisabled : ""}
                        ${isSelectedYear ? cellSelected : ""}
                        ${!isSelectedYear && !disabled ? cellHover : ""}
                        ${isTodayYear && !isSelectedYear ? cellToday : ""}
                      `}
                >
                  {year}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
